import { useEffect, useMemo, useState } from "react"

import { AppShell } from "@/components/app-shell"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { BleConnectionPanel, type DataSource } from "@/components/devices/ble-connection-panel"
import { ReadingsPanel } from "@/components/devices/readings-tab/readings-panel"
import { SensorsPanel } from "@/components/devices/sensors-tab/sensors-panel"
import { CalibrationPanel } from "@/components/devices/calibration-tab/calibration-panel"
import { SettingsPanel } from "@/components/devices/settings-tab/settings-panel"
import { useBleDevice } from "@/hooks/use-ble-device"
import { useCalibration } from "@/hooks/use-calibration"
import { useDeviceStatus } from "@/hooks/use-device-status"
import { toReadingPoint } from "@/lib/ble/parsers"
import type { Device, DeviceReading, SensorReading } from "@/types/device"

interface DevicesShowProps {
  device: Device
  readings: DeviceReading[]
}

export default function DevicesShow({ device, readings }: DevicesShowProps) {
  const ble = useBleDevice()
  const calibration = useCalibration(device.calibration_data)
  const { getLatestReading, isConnected, seedHistory, getReadingsHistory } = useDeviceStatus()
  const [dataSource, setDataSource] = useState<DataSource>("http")

  // Seed the readings buffer from server-rendered history so the sparkline
  // has content on first paint.
  useEffect(() => {
    seedHistory(device.id, readings.map(toReadingPoint))
  }, [device.id, readings, seedHistory])

  const isBleConnected = ble.state.status === "connected"
  const isHttpConnected = isConnected(device.id)

  const httpReading = getLatestReading(device.id)
  const history = getReadingsHistory(device.id)

  const reading: SensorReading = useMemo(() => {
    if (dataSource === "bluetooth") {
      return ble.reading
    }

    if (httpReading) return httpReading

    // No live reading — fall back to the most recent historical point so
    // panels (calibration, readings hero) have something to display when
    // the device is between samples.
    const last = history[history.length - 1]
    if (last) {
      return {
        specificGravity: last.sg,
        temperature: last.temp,
        tiltAngle: last.tilt,
        batteryVoltage: last.battery,
        accelerometer: null,
        pth: null,
      }
    }

    return {
      specificGravity: null,
      temperature: null,
      tiltAngle: null,
      batteryVoltage: null,
      accelerometer: null,
      pth: null,
    }
  }, [dataSource, httpReading, ble.reading, history])

  return (
    <AppShell title={device.name} backTo="/devices">
      <div className="px-4 lg:px-6">
        <div className="hidden md:flex items-baseline gap-2 mb-6">
          <h1 className="text-2xl font-semibold">{device.name}</h1>
          <span className="text-muted-foreground text-sm">
            {device.device_type} hydrometer
          </span>
        </div>

        <div className="mb-6">
          <BleConnectionPanel
            state={ble.state}
            isSupported={ble.isSupported}
            isHttpConnected={isHttpConnected}
            dataSource={dataSource}
            onDataSourceChange={setDataSource}
            onScan={ble.scan}
            onDisconnect={ble.disconnect}
          />
        </div>

        <Tabs defaultValue="readings" className="space-y-4">
          <div className="-mx-4 overflow-x-auto px-4 scroll-smooth snap-x snap-mandatory md:mx-0 md:overflow-visible md:px-0">
            <TabsList className="w-max md:w-fit">
              <TabsTrigger className="snap-start" value="readings">Readings</TabsTrigger>
              <TabsTrigger className="snap-start" value="sensors">Sensors</TabsTrigger>
              <TabsTrigger className="snap-start" value="calibration">Calibration</TabsTrigger>
              <TabsTrigger className="snap-start" value="settings">Settings</TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value="readings">
            <ReadingsPanel
              deviceId={device.id}
              reading={reading}
              settings={device.settings}
              lastSeenAt={device.last_seen_at}
            />
          </TabsContent>

          <TabsContent value="sensors">
            <SensorsPanel reading={reading} />
          </TabsContent>

          <TabsContent value="calibration">
            <CalibrationPanel
              device={device}
              reading={reading}
              points={calibration.points}
              onAddPoint={calibration.addPoint}
              onRemovePoint={calibration.removePoint}
              onClearPoints={calibration.clearPoints}
              isConnected={isBleConnected}
            />
          </TabsContent>

          <TabsContent value="settings">
            <SettingsPanel device={device} />
          </TabsContent>
        </Tabs>
      </div>
    </AppShell>
  )
}
